/**
 * /portal/activate?token=xxx — accept an org invite.
 * Works for both email invites (email prefilled) and link invites (user enters email).
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Lock, Mail, CheckCircle2, AlertCircle, Sparkles } from 'lucide-react';
import axios from 'axios';
import { usePortalStore, toast } from '../../stores';
import { onboardingApi } from '../../services/onboarding';
import { NexusButton } from '../../components/nexus-ui/NexusButton';
import { NexusInput } from '../../components/nexus-ui/NexusInput';
import { NexusCard } from '../../components/nexus-ui/NexusCard';

const PortalActivate = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [info, setInfo] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [inviteError, setInviteError] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { setAuth } = usePortalStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setInviteError('Missing activation token'); setLoadingInvite(false); return; }
    onboardingApi.verifyInvite(token)
      .then(({ data }) => { setInfo(data); setEmail(data.email || ''); })
      .catch((err) => setInviteError(err?.response?.data?.detail || 'Invalid or expired invite'))
      .finally(() => setLoadingInvite(false));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSubmitting(true);
    try {
      let data;
      if (info.mode === 'email') {
        ({ data } = await onboardingApi.acceptInvite({ token, name, password }));
      } else {
        // Link invite: POST /invites/accept-link/{token}?email=&name=&password= (query params per backend)
        const API = process.env.REACT_APP_BACKEND_URL;
        const resp = await axios.post(`${API}/api/portal/invites/accept-link/${token}`, null, {
          params: { email, name, password },
        });
        data = resp.data;
      }
      setAuth({ token: data.access_token, user: data.user, tenant: data.tenant, authType: 'jwt' });
      toast.success(`Welcome to ${data.tenant.name}`);
      navigate('/portal/dashboard', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not activate — ' + (err?.message || ''));
    } finally { setSubmitting(false); }
  };

  if (loadingInvite) {
    return <div className="min-h-screen flex items-center justify-center p-8 text-nexus-text-tertiary">Verifying invite…</div>;
  }
  if (inviteError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--nexus-bg-primary)' }}>
        <NexusCard variant="glass" hover={false} className="p-8 max-w-md w-full text-center">
          <div className="inline-flex w-14 h-14 rounded-full items-center justify-center mb-3"
            style={{ background: 'rgba(239,68,68,0.12)' }}>
            <AlertCircle size={28} style={{ color: '#EF4444' }} />
          </div>
          <h2 className="font-heading text-xl font-semibold text-nexus-text-primary">Invite problem</h2>
          <p className="text-[13px] text-nexus-text-secondary mt-1.5">{inviteError}</p>
          <div className="mt-5 flex gap-2 justify-center">
            <Link to="/portal/login" className="px-4 py-2 rounded-lg text-[12px] font-semibold text-white" style={{ background: '#0A1B2E' }}>Sign in</Link>
            <Link to="/portal/register" className="px-4 py-2 rounded-lg text-[12px] font-semibold text-white" style={{ background: '#F26B1F' }}>Create organization</Link>
          </div>
        </NexusCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--nexus-bg-primary)' }}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <NexusCard variant="glass" hover={false} className="p-8">
          <div className="text-center mb-6">
            <div className="inline-flex w-14 h-14 rounded-full items-center justify-center mb-3"
              style={{ background: 'rgba(110,231,183,0.12)' }}>
              <CheckCircle2 size={28} style={{ color: '#10B981' }} />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-nexus-text-primary">You're invited</h1>
            <p className="text-[13px] text-nexus-text-secondary mt-1.5">
              {info.invited_by_name} invited you to join <b>{info.tenant_name}</b> as a <b>{info.role}</b>.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            <NexusInput label="Email" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={info.mode === 'email'} icon={<Mail size={14} />}
              required data-testid="activate-email" />
            <NexusInput label="Your name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Priya Analyst" icon={<User size={14} />} required data-testid="activate-name" autoFocus />
            <NexusInput label="Set a password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters" icon={<Lock size={14} />} error={error} minLength={8} required
              data-testid="activate-password" />
            <NexusButton type="submit" className="w-full" isLoading={submitting} data-testid="activate-submit">
              <Sparkles size={14} /> Activate account
            </NexusButton>
          </form>
        </NexusCard>
      </motion.div>
    </div>
  );
};

export default PortalActivate;
